import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { UserFormState } from '@Enums/global/userprofile-userform-state.enum';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UpdateAccountDistributionRequest, UpdateUserAccountAccessRequest } from '@App/manage-users/models/update-user-account-access-request.interface';
import { FeatureToggle } from '@Enums';
import { FeatureManagerService, UserAdminService } from '@Services';
import { SharedModule } from '@Shared/shared.module';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import {MaskitoOptions} from '@maskito/core';
import {MaskitoDirective} from '@maskito/angular';
import { AppService } from '@App';

@Component({
  selector: 'app-edit-users-client-accounts',
  templateUrl: './edit-users-client-accounts.component.html',
  styleUrls: ['./edit-users-client-accounts.component.scss'],
  imports: [SharedModule, MaskitoDirective]
})
export class EditUsersClientAccountsComponent {
  @Input() userId: string;
  @Input() userRoleId: string;
  @Input() editedUserClientAccounts: any;
  @Input() loggedInUserAccounts: any;
  @Input() isReadOnly: boolean;
  _expandClientAccounts: boolean;
  @Output() formSaveStateChanged = new EventEmitter<UserFormState>();

  editedUserAccountsCopy: any[] = [];
  originalValues: Record<string, any> = {};
  displayedColumns: string[];
  maskitoOptions: MaskitoOptions;
  canUpdateAccounts = false;

  @Input()
  set expandClientAccounts(expandClientAccounts: boolean) {
    this.isExpanded = expandClientAccounts
  }
  get expandClientAccounts(): boolean {
    return this._expandClientAccounts;
  }

  @Output() updateSaved = new EventEmitter<void>();

  constructor (
    private readonly userAdminService: UserAdminService,
    private readonly featureManagerService: FeatureManagerService,
    private readonly appService: AppService
  ) {}

  isExpanded = false;
  checkedAccountsCount = 0;

  ngOnInit() {
    this.canUpdateAccounts = this.appService.checkACL('User Management: Account Assignment');
    this.maskitoOptions = maskitoNumberOptionsGenerator({
      maximumFractionDigits: 0,
      min: 0,
      thousandSeparator: ',',
      prefix: "$",
  });
    this.setupTableColumns();
    const helper = this.editedUserClientAccounts[0];
    if (helper?.accounts.length > 0) {
      this.editedUserAccountsCopy = helper?.accounts;
      this.checkedAccountsCount = helper?.accounts.filter(a => this.loggedInUserAccounts.map(a => a.id).includes(a.id)).length;
    }
  }

  setupTableColumns() {    
    const isDistributionEnabled = this.featureManagerService.getByName(FeatureToggle.GlobalUsersEnableEditDistributionUser)?.enabled;
          
    if (isDistributionEnabled) {
      this.displayedColumns = ['accountAccess', 'distribution', 'cosign', 'approval-limit', 'case-limit'];
    }
    else {
      this.displayedColumns = ['accountAccess'];
    }  
  }

  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  userHasAccountAccess(accountId: string) {
    const match = this.editedUserAccountsCopy?.find(a => a.id === accountId);
    if (match) {
      return true;
    }
    return false;
  }

  onAccountCheckboxChange(event: MatCheckboxChange, clientId: string) {
    const isChecked = event.checked;
    const accountId = event.source.id; // id is accountId

    const request: UpdateUserAccountAccessRequest = {
        userId : this.userId, 
        clientId: clientId, 
        accountId: accountId, 
        userRoleId: this.userRoleId,
        hasAccess : isChecked
    }
    this.formSaveStateChanged.emit(UserFormState.Saving); 
    this.userAdminService.updateUserAccountAccess(request, true).subscribe(result => {
      if (result === true) {
        this.formSaveStateChanged.emit(UserFormState.Saved); 
        if (request.hasAccess) {
          this.checkedAccountsCount += 1;
          this.editedUserAccountsCopy.push({
            id: request.accountId
          });
        }
        else if (!request.hasAccess) {
          this.checkedAccountsCount -= 1;
          this.editedUserAccountsCopy = this.editedUserAccountsCopy.filter(a => a.id !== request.accountId);
        }
        this.updateSaved.emit();
      }
      else {
        this.formSaveStateChanged.emit(UserFormState.Error); 
      }
    });
  }

  userIsOnDistributionForAccount(accountId: string) {
    const match = this.editedUserClientAccounts[0]?.accounts.find(a => a.id === accountId);
    if (!match) {
      return false;
    }
    return match.activeDistribution ?? false;
  }

  userIsOnForCoSignForAccount(accountId: string) {
    const match = this.editedUserClientAccounts[0]?.accounts.find(a => a.id === accountId);
    if (!match) {
      return false;
    }
    return match.activeCoSign ?? false;
  }

  userApprovalLimitForAccount(accountId: string) {
    const match = this.editedUserClientAccounts[0]?.accounts.find(a => a.id === accountId);
    if (!match) {
      return false;
    }

    if (match.approvalLimit) {
      return match.approvalLimit;
    }
    else {
      return 0;
    }
  }

  userApprovalLimitForAccountFormatted(accountId: string) {
    const approvalLimit = this.userApprovalLimitForAccount(accountId);
    return approvalLimit ? `$${Number(approvalLimit).toLocaleString()}` : '$0';
  }


  userCaseLimitForAccount(accountId: string) {
    const match = this.editedUserClientAccounts[0]?.accounts.find(a => a.id === accountId);
    if (!match) {
      return false;
    }

    if (match.caseLimit) {
      return match.caseLimit;
    }
    else {
      return null;
    }
  }

  onFieldFocus(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    this.originalValues[inputElement.id] = inputElement.value;
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();
  }

  toggleAccountDistribution(event: MatSlideToggleChange, account: any) {
    const request:UpdateAccountDistributionRequest = {
      userId: this.userId,
      clientId: account.clientId, 
      accountId: account.id,
      isActive: event.checked,
      isCoSignActive: this.userIsOnForCoSignForAccount(account.id),
      approvalLimit: this.userApprovalLimitForAccount(account.id),
      caseLimit: this.userCaseLimitForAccount(account.id),
      eventType: ''
    }

    request.eventType = request.isActive ? 'Distribution Enabled' : 'Distribution Disabled';

    this.formSaveStateChanged.emit(UserFormState.Saving);
    this.userAdminService.updateAccountDistribution(request, true).subscribe(result => {
      if (result === true) {
        const helper = this.editedUserAccountsCopy.find(a => a.id === account.id)
        if (helper) // this should always be true; distribution can only be toggled if edited user has access to the account
        {
          helper.activeDistribution = request.isActive;
        }
        this.updateSaved.emit();
        this.formSaveStateChanged.emit(UserFormState.Saved);
      }
      else {
        this.formSaveStateChanged.emit(UserFormState.Error);
      }
    });
  }

  toggleAccountCoSign(event: MatSlideToggleChange, account: any) {
    const request:UpdateAccountDistributionRequest = {
      userId: this.userId,
      clientId: account.clientId, 
      accountId: account.id,
      isActive: this.userIsOnDistributionForAccount(account.id),
      isCoSignActive: event.checked,
      approvalLimit: this.userApprovalLimitForAccount(account.id),
      caseLimit: this.userCaseLimitForAccount(account.id),
      eventType: ''
    }

    request.eventType = request.isCoSignActive ? 'Co-Sign Enabled' : 'Co-Sign Disabled';

    this.formSaveStateChanged.emit(UserFormState.Saving);
    this.userAdminService.updateAccountDistribution(request, true).subscribe(result => {
      if (result === true) {
        const helper = this.editedUserAccountsCopy.find(a => a.id === account.id)
        if (helper) // this should always be true; cosign can only be toggled if edited user has access to the account
        {
          helper.activeCoSign = request.isCoSignActive;
        }
        this.updateSaved.emit();
        this.formSaveStateChanged.emit(UserFormState.Saved);
      }
      else {
        this.formSaveStateChanged.emit(UserFormState.Error);
      }
    });
  }

  updateApprovalLimit(event: FocusEvent, account: any) {
    const inputElement = event.target as HTMLInputElement;
    if (this.originalValues[inputElement.id] === inputElement.value)
      return;
    const approvalLimit = this.sanitizeApprovalLimit(inputElement.value);
    const request:UpdateAccountDistributionRequest = {
      userId: this.userId,
      clientId: account.clientId, 
      accountId: account.id,
      isActive: this.userIsOnDistributionForAccount(account.id),
      isCoSignActive: this.userIsOnForCoSignForAccount(account.id),
      approvalLimit: approvalLimit,
      caseLimit: this.userCaseLimitForAccount(account.id),
      eventType: 'Authority Limit Updated'
    }

    this.formSaveStateChanged.emit(UserFormState.Saving);
    this.userAdminService.updateAccountDistribution(request, true).subscribe(result => {
      if (result === true) {
        const helper = this.editedUserAccountsCopy.find(a => a.id === account.id)
        if (helper) // this should always be true; distribution can only be toggled if edited user has access to the account
        {
          helper.approvalLimit = request.approvalLimit;
        }
        this.formSaveStateChanged.emit(UserFormState.Saved);
        this.updateSaved.emit();
      }
      else {
        this.formSaveStateChanged.emit(UserFormState.Error);
      }
    });
  }

  sanitizeApprovalLimit(value: string): number {
    return Number(value.replace(/[$,]/g, ""));
  }

  updateCaseLimit(event: InputEvent, account: any) {
    const inputElement = event.target as HTMLInputElement;
    if (this.originalValues[inputElement.id] === inputElement.value)
      return;
    const caseLimit = inputElement.value === '' ? null : Number(inputElement.value);
    const request:UpdateAccountDistributionRequest = {
      userId: this.userId,
      clientId: account.clientId, 
      accountId: account.id,
      isActive: this.userIsOnDistributionForAccount(account.id),
      isCoSignActive: this.userIsOnForCoSignForAccount(account.id),
      approvalLimit: this.userApprovalLimitForAccount(account.id),
      caseLimit: caseLimit,
      eventType: 'Case Limit Updated'
    }

    this.formSaveStateChanged.emit(UserFormState.Saving);
    this.userAdminService.updateAccountDistribution(request, true).subscribe(result => {
      if (result === true) {
        const helper = this.editedUserAccountsCopy.find(a => a.id === account.id)
        if (helper) // this should always be true; distribution can only be toggled if edited user has access to the account
        {
          helper.caseLimit = request.caseLimit;
        }
        this.formSaveStateChanged.emit(UserFormState.Saved);
        this.updateSaved.emit();
      }
      else {
        this.formSaveStateChanged.emit(UserFormState.Error);
      }
    });
  }
}
